import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Bridges the service worker's `notificationclick` back into the SPA router.
 *
 * `public/firebase-messaging-sw.js` handles a background notification click in
 * two ways: with no tab open it calls `clients.openWindow(data.url)`, which
 * navigates fine; with a tab already open it focuses that tab and posts a
 * `NOTIFICATION_CLICK` message. Nothing listened for that message, so clicking
 * a notification while the panel was open just raised the window and left the
 * admin wherever they were. This hook completes that path.
 *
 * `data.url` is produced by the backend
 * (`notificationRouting.service.ts#resolveWebPath`); admin notifications
 * resolve to `/admin/notifications`, so the paths are normalised to the
 * admin-panel's own routes, which are not `/admin`-prefixed.
 */
export function useNotificationClickBridge(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const payload = event.data;
      if (!payload || payload.type !== 'NOTIFICATION_CLICK') return;

      const url: unknown = payload.data?.url;
      if (typeof url !== 'string' || !url.startsWith('/')) return;

      // The backend speaks in figano-web paths; this app is mounted at the
      // root of a different host, so strip the panel prefix.
      const target = url.startsWith('/admin/') ? url.slice('/admin'.length) : url;
      navigate(target);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
}
